/** Map a DRF ValidationError body onto per-field messages.
 *
 * The mapping endpoints raise `ValidationError({"custom_id": "..."})`, so a
 * rejected edit knows exactly which input was wrong. Surfacing it on the field
 * rather than in a snackbar matters here: a batch of edits can fail on one
 * value among several, and a toast loses which one.
 */
import { ConsoleApiError } from '@/api/client';

const KNOWN_FIELDS = [
  'custom_id',
  'id_type',
  'use_for_matching',
  'api_queryable',
  'integration',
  'restech_account',
  'tour_id',
  'object_id',
  'tag_ids',
  'target_ids',
  'tour_operator_id',
  'model',
  'id',
] as const;

export function fieldErrorsOf(e: unknown): Record<string, string> {
  if (!(e instanceof ConsoleApiError) || !e.body || typeof e.body !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(e.body as Record<string, unknown>)) {
    if (!(KNOWN_FIELDS as readonly string[]).includes(key)) continue;
    const msg = Array.isArray(raw) ? raw.join(' ') : String(raw);
    if (msg) out[key] = msg;
  }
  return out;
}

/** The server's refusal to touch an import-owned (managed) alias — a 400
 *  `{managed: "..."}` on an edit or delete that did not take it over first — or
 *  null. The row was managed when the server looked, whatever the page showed. */
export function managedRefusalOf(e: unknown): string | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const raw = (e.body as { managed?: unknown } | undefined)?.managed;
  const msg = Array.isArray(raw) ? raw.join(' ') : typeof raw === 'string' ? raw : '';
  return msg || null;
}

/** Everything the body said, for the cases with no field to hang it on. */
export function messageOf(e: unknown, fallback: string): string {
  if (!(e instanceof ConsoleApiError)) return fallback;
  const detail = (e.body as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return managedRefusalOf(e) ?? (e.message || fallback);
}
